import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { setActiveVehicle } from "../../lib/fitment";
import { ChevronRight } from "lucide-react";

/** Cascading Year → Make → Model → Trim selector. */
export default function YMMTSelector({ variant = "hero", onVehicleResolved }) {
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [trim, setTrim] = useState("");

  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles/years").then(({ data }) => setYears(data.years));
  }, []);

  useEffect(() => {
    if (!year) { setMakes([]); setMake(""); return; }
    api.get(`/vehicles/makes?year=${year}`).then(({ data }) => setMakes(data.makes));
    setMake(""); setModel(""); setTrim("");
  }, [year]);

  useEffect(() => {
    if (!year || !make) { setModels([]); setModel(""); return; }
    api.get(`/vehicles/models?year=${year}&make=${make}`).then(({ data }) => setModels(data.models));
    setModel(""); setTrim("");
  }, [year, make]);

  useEffect(() => {
    if (!year || !make || !model) { setTrims([]); setTrim(""); return; }
    api.get(`/vehicles/trims?year=${year}&make=${make}&model=${model}`).then(({ data }) => setTrims(data.trims));
    setTrim("");
  }, [year, make, model]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!year || !make || !model) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), make, model });
      if (trim) params.append("trim", trim);
      const { data } = await api.get(`/vehicles/lookup?${params.toString()}`);
      if (onVehicleResolved) {
        onVehicleResolved(data);
      } else {
        const trimName = trim ? (trims.find((t) => t.id === trim)?.name || "") : "";
        setActiveVehicle({
          vehicle_id: data.vehicle.id,
          label: `${year} ${data.brand.name} ${data.model.name}`,
          year,
          make: data.brand.name,
          model: data.model.name,
          trim: trimName,
        });
        navigate(`/vehicle/${data.vehicle.id}?label=${encodeURIComponent(`${year} ${data.brand.name} ${data.model.name}`)}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isHero = variant === "hero";
  const baseSelect = isHero
    ? "w-full h-12 px-4 bg-white/5 border border-white/10 text-white text-sm rounded-sm focus:outline-none focus:border-red-500 transition appearance-none cursor-pointer"
    : "w-full h-10 px-3 bg-white/5 border border-white/10 text-white text-sm rounded-sm focus:outline-none focus:border-red-500 transition appearance-none cursor-pointer";
  const label = "text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block";

  return (
    <form
      data-testid="ymmt-selector"
      onSubmit={handleSubmit}
      className={isHero
        ? "glass p-6 md:p-8 rounded-lg w-full"
        : "bg-white/5 border border-white/10 p-5 rounded-md"}
    >
      {isHero && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-2">Find your fit</div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight">Select your vehicle</h2>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className={label}>Year</label>
          <select data-testid="ymmt-year" value={year} onChange={(e) => setYear(e.target.value)} className={baseSelect}>
            <option value="">Select</option>
            {years.map((y) => <option key={y} value={y} className="bg-zinc-900">{y}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Make</label>
          <select data-testid="ymmt-make" value={make} onChange={(e) => setMake(e.target.value)} disabled={!year} className={baseSelect}>
            <option value="">Select</option>
            {makes.map((m) => <option key={m.id} value={m.slug} className="bg-zinc-900">{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Model</label>
          <select data-testid="ymmt-model" value={model} onChange={(e) => setModel(e.target.value)} disabled={!make} className={baseSelect}>
            <option value="">Select</option>
            {models.map((m) => <option key={m.id} value={m.slug} className="bg-zinc-900">{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Trim <span className="opacity-60">(optional)</span></label>
          <select data-testid="ymmt-trim" value={trim} onChange={(e) => setTrim(e.target.value)} disabled={!model} className={baseSelect}>
            <option value="">Any trim</option>
            {trims.map((t) => <option key={t.id} value={t.id} className="bg-zinc-900">{t.name}</option>)}
          </select>
        </div>
      </div>

      <button
        data-testid="ymmt-submit"
        type="submit"
        disabled={!year || !make || !model || loading}
        className={`mt-6 inline-flex items-center justify-center gap-2 h-12 px-8 bg-red-600 hover:bg-red-700 text-white font-medium tracking-wide transition disabled:opacity-40 disabled:cursor-not-allowed rounded-sm ${isHero ? "w-full md:w-auto" : "w-full"}`}
      >
        {loading ? "Searching…" : "Find compatible parts"}
        <ChevronRight size={16} />
      </button>
    </form>
  );
}
